import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useMutation } from '@apollo/client';
import Modal from '~/app/components/Modal';
import Button from '~/app/components/Button';
import UIButton from '~/app/ui/Button';
import { S, TONES, DARK } from '~/app/ui/tokens';

const CREATE_CUSTOM_WIDGET = gql`
  mutation CreateCustomWidget($input: CreateCustomWidgetInput!) {
    createCustomWidget(input: $input) {
      _id
      name
      title
      description
      category
      isActive
      createdAt
    }
  }
`;

const useStyles = createUseStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#0f172a',
    [DARK]: { color: S.fg },
  },
  hint: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px',
    [DARK]: { color: S.muted },
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
    },
    [DARK]: {
      background: S.field,
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
    },
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    [DARK]: { background: TONES.red.bg, borderColor: TONES.red.bg, color: TONES.red.fg },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: '10px',
  },
  namePreview: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'monospace',
    background: '#f8fafc',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
    [DARK]: { color: S.muted, background: S.chrome },
  },
});

interface Props {
  onClose: () => void;
  onSuccess: (widget: any) => void;
}

const CreateWidgetModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const c = useStyles();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createWidget, { loading }] = useMutation(CREATE_CUSTOM_WIDGET, {
    onCompleted: (data) => {
      onSuccess(data.createCustomWidget);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleNameChange = (value: string) => {
    // Auto-format name: only allow letters, numbers, hyphens, underscores
    const formatted = value
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .replace(/^[^a-zA-Z]+/, ''); // Must start with letter
    setName(formatted);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Widget name is required');
      return;
    }

    if (!title.trim()) {
      setError('Widget title is required');
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(name)) {
      setError('Name must start with a letter and contain only letters, numbers, hyphens, and underscores');
      return;
    }

    createWidget({
      variables: {
        input: {
          name: name.trim(),
          title: title.trim(),
          description: description.trim() || undefined,
          category: 'Custom',
        },
      },
    });
  };

  const widgetId = name ? `Custom_${name}` : 'Custom_YourWidgetName';

  return (
    <Modal title="Create New Widget" onClose={onClose}>
      <form className={c.form} onSubmit={handleSubmit}>
        {error && <div className={c.error}>{error}</div>}

        <div className={c.field}>
          <label className={c.label}>Widget Name *</label>
          <input
            type="text"
            className={c.input}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., HeroSection, FeatureCard"
            autoFocus
          />
          <div className={c.hint}>
            Unique identifier. Letters, numbers, hyphens, and underscores only.
          </div>
          {name && (
            <div className={c.namePreview}>
              Widget ID: <strong>{widgetId}</strong>
            </div>
          )}
        </div>

        <div className={c.field}>
          <label className={c.label}>Display Title *</label>
          <input
            type="text"
            className={c.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Hero Section, Feature Card"
          />
          <div className={c.hint}>
            Human-readable name shown in the widget browser.
          </div>
        </div>

        <div className={c.field}>
          <label className={c.label}>Description</label>
          <textarea
            className={c.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this widget does..."
            rows={3}
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className={c.actions}>
          <Button secondary onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <UIButton type="submit" variant="primary" busy={loading} disabled={!name || !title}>
            {loading ? 'Creating…' : 'Create widget'}
          </UIButton>
        </div>
      </form>
    </Modal>
  );
};

export default CreateWidgetModal;

